import React, { useEffect, useState } from 'react';
import { App } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { QUERY_KEYS } from '@/api/queryKeys';
import {
  stockReconciliation,
  unwrapStockReconciliationQueueItem,
  useStockReconciliationRun,
} from '@/api/StockReconciliation';
import { queryClient } from '@/config/reactQuery';
import { useHttpAuthReady } from '@/hooks';
import { SystemAdministrationRoutes } from '@/routes';
import { useTranslate } from '@/translations';
import { Loader, PageContainer } from '@/ui/components';
import {
  prepareStockUploadRows,
  STOCK_UPLOAD_PAGE_SIZE,
  StockUploadColumns,
} from './prepareStockUploadRows';
import { StockReconciliationResultView } from './StockReconciliationResult';
import { StockReconciliationSetup } from './StockReconciliationSetup';

export const StockReconciliation: React.FC = () => {
  const { t } = useTranslate();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { id } = useParams();
  const isHttpAuthReady = useHttpAuthReady();
  const savedId = Number(id);
  const hasSavedId = Number.isFinite(savedId) && savedId > 0;
  const {
    data: saved,
    isError: savedFailed,
    isLoading: savedLoading,
  } = useStockReconciliationRun(hasSavedId ? savedId : undefined, {
    enabled: isHttpAuthReady && hasSavedId,
  });
  const [submitting, setSubmitting] = useState(false);
  const [uploadLabel, setUploadLabel] = useState<string>();
  const [runError, setRunError] = useState<string>();

  useEffect(() => {
    if (savedFailed) {
      message.error(t('stockReconciliationLoadFailed'));
      navigate(SystemAdministrationRoutes.stockReconciliation);
    }
  }, [message, navigate, savedFailed, t]);

  const start = async (values: {
    customerId: number;
    customerName: string;
    locationId: number;
    locationName: string;
    reconciliationAt: string;
    file: File;
    rows: Record<string, string>[];
    columns: StockUploadColumns;
  }) => {
    try {
      setRunError(undefined);
      const rows = prepareStockUploadRows(values.rows, values.columns);
      if (!rows.length) {
        const text = t('stockReconciliationInvalidFile');
        setRunError(text);
        message.error(text);
        return;
      }
      setSubmitting(true);
      setUploadLabel(
        t('stockReconciliationUploading', { done: 0, total: rows.length }),
      );
      const started = unwrapStockReconciliationQueueItem(
        await stockReconciliation.startUpload({
          customerId: values.customerId,
          customerName: values.customerName,
          locationId: values.locationId,
          locationName: values.locationName,
          reconciliationAt: values.reconciliationAt,
          fileName: values.file.name,
        }),
      );
      if (!started?.id || started.status === 'Failed') {
        const text = started?.message || t('stockReconciliationRunFailed');
        setRunError(text);
        message.error(text);
        return;
      }
      try {
        for (
          let index = 0;
          index < rows.length;
          index += STOCK_UPLOAD_PAGE_SIZE
        ) {
          const page = rows.slice(index, index + STOCK_UPLOAD_PAGE_SIZE);
          await stockReconciliation.appendRows(started.id, page);
          setUploadLabel(
            t('stockReconciliationUploading', {
              done: Math.min(index + page.length, rows.length),
              total: rows.length,
            }),
          );
        }
        const completed = unwrapStockReconciliationQueueItem(
          await stockReconciliation.completeUpload(started.id),
        );
        if (!completed?.id || completed.status === 'Failed') {
          const text = completed?.message || t('stockReconciliationRunFailed');
          setRunError(text);
          message.error(text);
          return;
        }
        await queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.stockReconciliationQueue],
        });
        await queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.stockReconciliationHistory],
        });
        message.success(t('stockReconciliationQueued'));
      } catch {
        await stockReconciliation.failUpload(started.id).catch(() => undefined);
        const text = t('stockReconciliationRunFailed');
        setRunError(text);
        message.error(text);
      }
    } catch {
      const text = t('stockReconciliationRunFailed');
      setRunError(text);
      message.error(text);
    } finally {
      setSubmitting(false);
      setUploadLabel(undefined);
    }
  };

  if (hasSavedId && !saved && savedLoading) {
    return (
      <PageContainer>
        <div data-testid="stock-reconciliation-loading">
          <Loader />
        </div>
      </PageContainer>
    );
  }

  if (hasSavedId && saved) {
    return (
      <StockReconciliationResultView
        result={saved}
        onBack={() => navigate(SystemAdministrationRoutes.stockReconciliation)}
      />
    );
  }

  return (
    <StockReconciliationSetup
      submitting={submitting}
      submitLabel={uploadLabel}
      error={runError}
      onSubmit={start}
      onOpenRun={(runId) =>
        navigate(`${SystemAdministrationRoutes.stockReconciliation}/${runId}`)
      }
    />
  );
};
